import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import './SEOManager.css';

const SEOManager = () => {
  const [seoSettings, setSeoSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState('home');
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    page: 'home',
    title: '',
    description: '',
    keywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    canonicalUrl: '',
    robots: 'index, follow',
    structuredData: null,
    isActive: true,
  });
  const [uploading, setUploading] = useState(false);
  const [showStructuredData, setShowStructuredData] = useState(false);
  const [helpModal, setHelpModal] = useState({ show: false, title: '', content: '' });

  const token = localStorage.getItem('token');

  const pages = [
    { value: 'home', label: 'Home Page' },
    { value: 'products', label: 'Products Listing' },
    { value: 'product-detail', label: 'Product Detail' },
    { value: 'blog', label: 'Blog Listing' },
    { value: 'blog-detail', label: 'Blog Detail' },
    { value: 'workshops', label: 'Workshops Listing' },
    { value: 'workshop-detail', label: 'Workshop Detail' },
    { value: 'category', label: 'Category Page' },
    { value: 'about', label: 'About Page' },
    { value: 'contact', label: 'Contact Page' },
  ];

  // SEO Examples for each page type
  const seoExamples = {
    'home': {
      title: 'Shilpani - Premium Resin Art & Concrete Creations | Handcrafted Home Decor',
      description: 'Discover premium handcrafted resin art and concrete home decor items. Transform your space with unique, Vastu-compliant designs. Shop now for exclusive art frames and spiritual decor.',
      keywords: 'resin art, concrete decor, handcrafted home decor, art frames, Vastu art, spiritual decor, custom art, India art, premium frames, home decoration',
      ogTitle: 'Shilpani - Premium Resin Art & Concrete Creations',
      ogDescription: 'Transform your space with premium handcrafted resin art and concrete decor. Shop exclusive Vastu-compliant designs.',
      canonicalUrl: 'https://shilpani.com'
    },
    'products': {
      title: 'Shop All Products - Premium Resin Art & Concrete Decor | Shilpani',
      description: 'Browse our complete collection of premium resin art and concrete home decor items. Handcrafted with love, Vastu-compliant designs for every space.',
      keywords: 'resin art products, concrete decor items, art frames collection, home decor products, Vastu art items, spiritual decor, custom art pieces',
      ogTitle: 'Shop Premium Resin Art & Concrete Decor - Shilpani',
      ogDescription: 'Browse our complete collection of handcrafted resin art and concrete decor items.',
      canonicalUrl: 'https://shilpani.com/products'
    },
    'product-detail': {
      title: '{Product Name} - Premium Resin Art | Shilpani',
      description: 'Buy {Product Name} - Premium handcrafted resin art/concrete decor. {Brief product description}. Free shipping across India.',
      keywords: '{product name}, resin art, concrete decor, handcrafted, Vastu art, home decor, {category name}',
      ogTitle: '{Product Name} - Premium Handcrafted Art',
      ogDescription: '{Product Name} - Premium handcrafted resin art/concrete decor. Perfect for {use case}.',
      canonicalUrl: 'https://shilpani.com/products/{product-id}'
    },
    'blog': {
      title: 'Art & Decor Blog - Tips, Vastu Guide & Home Decor Ideas | Shilpani',
      description: 'Read our latest articles on art, Vastu tips, home decor ideas, and spiritual design. Get expert insights on transforming your space.',
      keywords: 'art blog, Vastu tips, home decor ideas, interior design, spiritual art, decor inspiration, design tips, Vastu guide',
      ogTitle: 'Art & Decor Blog - Tips & Inspiration | Shilpani',
      ogDescription: 'Expert tips on art, Vastu, and home decor. Transform your space with our design insights.',
      canonicalUrl: 'https://shilpani.com/blog'
    },
    'blog-detail': {
      title: '{Blog Title} - Art & Decor Blog | Shilpani',
      description: '{Blog excerpt or first 150 characters}. Read the full article on our blog for expert tips on art, Vastu, and home decor.',
      keywords: '{blog category}, {main keywords from blog}, art tips, Vastu guide, home decor, {related keywords}',
      ogTitle: '{Blog Title}',
      ogDescription: '{Blog excerpt - first 200 characters}',
      canonicalUrl: 'https://shilpani.com/blog/{blog-id}'
    },
    'workshops': {
      title: 'Workshops - Learn Art, Vastu & Home Decor | Shilpani',
      description: 'Join our expert-led workshops on resin art, Vastu principles, and home decor. Learn through live sessions and recorded content. Book your spot today!',
      keywords: 'workshops, art workshops, Vastu workshops, home decor classes, online workshops, resin art classes, live workshops, recorded workshops, art education',
      ogTitle: 'Workshops - Learn Art, Vastu & Home Decor | Shilpani',
      ogDescription: 'Join expert-led workshops on resin art, Vastu, and home decor. Live and recorded sessions available.',
      canonicalUrl: 'https://shilpani.com/workshops'
    },
    'workshop-detail': {
      title: '{Workshop Title} - Art Workshop | Shilpani',
      description: '{Workshop description or first 150 characters}. Join this expert-led workshop to learn {topic}. Book your spot now!',
      keywords: '{workshop title}, {workshop tags}, art workshop, Vastu workshop, home decor class, {contentType}, {instructor}',
      ogTitle: '{Workshop Title} - Art Workshop',
      ogDescription: '{Workshop description - first 200 characters}. Learn from expert instructors.',
      canonicalUrl: 'https://shilpani.com/workshops/{workshop-id}'
    },
    'category': {
      title: '{Category Name} Collection - Premium Art & Decor | Shilpani',
      description: 'Explore our {Category Name} collection. Premium handcrafted {category items} with Vastu-compliant designs. Shop now for exclusive pieces.',
      keywords: '{category name}, {category} art, {category} decor, handcrafted {category}, Vastu {category}, {category} collection',
      ogTitle: '{Category Name} Collection - Premium Art & Decor',
      ogDescription: 'Discover our exclusive {Category Name} collection. Handcrafted with love, Vastu-compliant designs.',
      canonicalUrl: 'https://shilpani.com/category/{category-id}'
    },
    'about': {
      title: 'About Us - Shilpani Art & Crafts | Our Story & Mission',
      description: 'Learn about Shilpani Art & Crafts - passionate artisans creating premium resin art and concrete decor. Discover our story, mission, and commitment to quality.',
      keywords: 'about Shilpani, art company, handcrafted art, resin art makers, concrete decor creators, art artisans, company story',
      ogTitle: 'About Shilpani - Premium Art & Crafts',
      ogDescription: 'Passionate artisans creating premium handcrafted resin art and concrete decor. Discover our story.',
      canonicalUrl: 'https://shilpani.com/about'
    },
    'contact': {
      title: 'Contact Us - Shilpani Art & Crafts | Get in Touch',
      description: 'Contact Shilpani Art & Crafts for custom orders, inquiries, or support. We\'re here to help you find the perfect art piece for your space.',
      keywords: 'contact Shilpani, custom art orders, art inquiries, customer support, art consultation, custom design',
      ogTitle: 'Contact Shilpani - Get in Touch',
      ogDescription: 'Have questions? Contact us for custom orders, inquiries, or support. We\'re here to help!',
      canonicalUrl: 'https://shilpani.com/contact'
    }
  };

  const loadExample = () => {
    const example = seoExamples[selectedPage];
    if (example) {
      setFormData({
        ...formData,
        title: example.title,
        description: example.description,
        keywords: example.keywords,
        ogTitle: example.ogTitle,
        ogDescription: example.ogDescription,
        canonicalUrl: example.canonicalUrl,
      });
      toast.info('Example SEO content loaded! Please customize it for your needs.');
    }
  };

  const showHelp = (title, content) => {
    setHelpModal({ show: true, title, content });
  };

  const closeHelp = () => {
    setHelpModal({ show: false, title: '', content: '' });
  };

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      // Only load settings, don't show toast on initial load or page change
      loadPageSettings(selectedPage, false);
    }
    // Note: loadPageSettings is intentionally not in dependencies to avoid infinite loops
    // eslint-disable-next-line
  }, [selectedPage]);

  const fetchSEOSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/seo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched SEO settings:', response.data);
      setSeoSettings(response.data.data || []);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch SEO settings');
      setSeoSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPageSettings = async (page, showToast = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/seo/${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data.data;
      if (data) {
        setFormData({
          page: data.page || page,
          title: data.title || '',
          description: data.description || '',
          keywords: data.keywords || '',
          ogTitle: data.ogTitle || '',
          ogDescription: data.ogDescription || '',
          ogImage: data.ogImage || '',
          twitterTitle: data.twitterTitle || '',
          twitterDescription: data.twitterDescription || '',
          twitterImage: data.twitterImage || '',
          canonicalUrl: data.canonicalUrl || '',
          robots: data.robots || 'index, follow',
          structuredData: data.structuredData || null,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
        setEditingPage(data);
        if (showToast) {
          toast.success(`Loaded SEO settings for ${page}`);
        }
      }
    } catch (error) {
      // Page doesn't exist yet, reset form for new entry
      if (error.response?.status !== 404) {
        console.error(`Error loading SEO settings for ${page}:`, error);
        if (showToast) {
          toast.error(error.response?.data?.message || 'Failed to load SEO settings');
        }
      }
      setFormData({
        page: page,
        title: '',
        description: '',
        keywords: '',
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        canonicalUrl: '',
        robots: 'index, follow',
        structuredData: null,
        isActive: true,
      });
      setEditingPage(null);
    }
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    
    // Add upload type for SEO images
    const queryString = '?uploadType=seo';

    try {
      const response = await axios.post(`${API_URL}/api/upload${queryString}`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const imageUrl = response.data.data.baseS3Url || response.data.data.url;
      
      if (type === 'og') {
        setFormData({ ...formData, ogImage: imageUrl });
      } else if (type === 'twitter') {
        setFormData({ ...formData, twitterImage: imageUrl });
      }
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      // Ensure isActive defaults to true if not explicitly set
      const submitData = {
        ...formData,
        isActive: formData.isActive !== undefined ? formData.isActive : true
      };
      
      console.log('Submitting SEO data:', submitData);
      
      const response = await axios.put(
        `${API_URL}/api/seo/${formData.page}`,
        submitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('SEO save response:', response.data);
      toast.success('SEO settings saved successfully!');
      await fetchSEOSettings();
      await loadPageSettings(formData.page, false); // Don't show toast on reload
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to save SEO settings');
      }
      return false;
    }
  };

  const handleStructuredDataChange = (e) => {
    try {
      const parsed = JSON.parse(e.target.value);
      setFormData({ ...formData, structuredData: parsed });
    } catch (error) {
      toast.error('Invalid JSON format');
    }
  };

  const resetForm = () => {
    loadPageSettings(selectedPage);
  };

  return (
    <div className="seo-manager">
      <div className="header">
        <h2>SEO Manager</h2>
        <p className="subtitle">Manage SEO settings for all pages</p>
      </div>

      <div className="seo-content">
        {/* Page Selector */}
        <div className="page-selector">
          <div className="page-selector-row">
            <div className="page-selector-left">
              <label>Select Page:</label>
              <select
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                className="page-select"
              >
                {pages.map((page) => (
                  <option key={page.value} value={page.value}>
                    {page.value === selectedPage ? `✓ ${page.label}` : page.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="page-selector-right">
              <button
                type="button"
                onClick={loadExample}
                className="btn-load-example"
                title="Load example SEO content for this page"
              >
                📝 Load Example
              </button>
            </div>
          </div>
        </div>

        {/* Current Settings Summary */}
        {editingPage && (
          <div className="current-settings-summary">
            <h3>Current Settings for: {pages.find(p => p.value === selectedPage)?.label}</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <strong>Title:</strong> {editingPage.title || 'Not set'}
              </div>
              <div className="summary-item">
                <strong>Status:</strong> 
                <span className={editingPage.isActive ? 'status-active' : 'status-inactive'}>
                  {editingPage.isActive ? '✓ Active' : '✗ Inactive'}
                </span>
              </div>
              <div className="summary-item">
                <strong>Description:</strong> {editingPage.description ? `${editingPage.description.substring(0, 60)}...` : 'Not set'}
              </div>
              <div className="summary-item">
                <strong>Keywords:</strong> {editingPage.keywords || 'Not set'}
              </div>
            </div>
          </div>
        )}

        {/* SEO Form */}
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }} 
          className="seo-form"
          noValidate
        >
          <div className="form-section">
            <h3>
              Basic SEO
              <button
                type="button"
                className="help-icon"
                onClick={() => showHelp(
                  'What is Basic SEO?',
                  `Basic SEO fields are the foundation of search engine optimization:

Page Title: Appears in browser tabs and search results. Keep it 50-60 characters. Include your brand name and main keyword.

Meta Description: The snippet shown in search results below the title. Keep it 150-160 characters. Make it compelling to encourage clicks!

Keywords: Comma-separated keywords related to your page. While less important than before, they still help search engines understand your content.

These are the most important fields - always fill them out!`
                )}
                title="What is Basic SEO?"
              >
                ❓
              </button>
            </h3>
            
            <div className="form-group">
              <label>Page Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={seoExamples[selectedPage]?.title || "Page Title (appears in browser tab)"}
                required
                className={formData.title ? 'has-value' : ''}
              />
              <small>
                {formData.title ? `${formData.title.length} characters` : 'Recommended: 50-60 characters'}
                {!formData.title && seoExamples[selectedPage] && (
                  <span className="example-hint"> Example: {seoExamples[selectedPage].title}</span>
                )}
              </small>
            </div>

            <div className="form-group">
              <label>Meta Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder={seoExamples[selectedPage]?.description || "Meta description (appears in search results)"}
                maxLength="160"
                required
                className={formData.description ? 'has-value' : ''}
              />
              <small>
                {formData.description.length}/160 characters
                {formData.description && formData.description.length > 160 && (
                  <span className="warning"> (Exceeds recommended length)</span>
                )}
                {!formData.description && seoExamples[selectedPage] && (
                  <span className="example-hint"> Example: {seoExamples[selectedPage].description.substring(0, 80)}...</span>
                )}
              </small>
            </div>

            <div className="form-group">
              <label>Keywords</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder={seoExamples[selectedPage]?.keywords || "keyword1, keyword2, keyword3"}
                className={formData.keywords ? 'has-value' : ''}
              />
              <small>
                Comma-separated keywords
                {!formData.keywords && seoExamples[selectedPage] && (
                  <span className="example-hint"> Example: {seoExamples[selectedPage].keywords.substring(0, 60)}...</span>
                )}
              </small>
            </div>

            <div className="form-group">
              <label>
                Canonical URL
                <button
                  type="button"
                  className="help-icon-inline"
                  onClick={() => showHelp(
                    'What is Canonical URL?',
                    `Canonical URL tells search engines which is the "official" version of a page when you have duplicate content or similar pages.

Example: If you have both https://shilpani.com/products and https://shilpani.com/products?page=1, you can set the canonical URL to the first one to avoid duplicate content issues.

Why it matters: Prevents search engines from indexing duplicate content, which can hurt your SEO rankings. Leave empty to use the default page URL.`
                  )}
                  title="What is Canonical URL?"
                >
                  ❓
                </button>
              </label>
              <input
                type="url"
                value={formData.canonicalUrl}
                onChange={(e) => setFormData({ ...formData, canonicalUrl: e.target.value })}
                placeholder="https://shilpani.com/page"
                className={formData.canonicalUrl ? 'has-value' : ''}
              />
              <small>
                {formData.canonicalUrl ? `Current: ${formData.canonicalUrl}` : 'Leave empty to use default page URL. Prevents duplicate content issues.'}
              </small>
            </div>

            <div className="form-group">
              <label>
                Robots Directive
                <button
                  type="button"
                  className="help-icon-inline"
                  onClick={() => showHelp(
                    'What is Robots Directive?',
                    `Robots directive tells search engines how to handle your page:

Index, Follow: Search engines can index this page and follow links (RECOMMENDED for most pages)

Index, NoFollow: Index the page but don't follow links on it

NoIndex, Follow: Don't index this page but follow links (useful for private pages)

NoIndex, NoFollow: Don't index and don't follow links (for pages you want completely hidden from search engines)

Why it matters: Controls whether your page appears in search results and whether search engines crawl links on the page.`
                  )}
                  title="What is Robots Directive?"
                >
                  ❓
                </button>
              </label>
              <select
                value={formData.robots}
                onChange={(e) => setFormData({ ...formData, robots: e.target.value })}
              >
                <option value="index, follow">Index, Follow (Recommended)</option>
                <option value="index, nofollow">Index, NoFollow</option>
                <option value="noindex, follow">NoIndex, Follow</option>
                <option value="noindex, nofollow">NoIndex, NoFollow</option>
              </select>
              <small>
                Controls search engine indexing. "Index, Follow" is recommended for most pages.
              </small>
            </div>
          </div>

          <div className="form-section">
            <h3>
              Open Graph (Facebook & Social Media)
              <button
                type="button"
                className="help-icon"
                onClick={() => showHelp(
                  'What is Open Graph (OG)?',
                  `Open Graph (OG) tags control how your page appears when shared on social media platforms like Facebook, LinkedIn, WhatsApp, etc.

OG Title: The title shown when someone shares your page on social media. If left empty, the Page Title will be used.

OG Description: The description text shown below the title when shared. If left empty, the Meta Description will be used.

OG Image: The image displayed when your page is shared. This is crucial for attracting clicks! Recommended size: 1200x630 pixels. If left empty, a default image will be used.

Why it matters: When someone shares your page on Facebook, WhatsApp, or LinkedIn, these tags determine what preview card they see. A good OG image can significantly increase click-through rates!`
                )}
                title="What is Open Graph?"
              >
                ❓
              </button>
            </h3>
            
            <div className="form-group">
              <label>OG Title</label>
              <input
                type="text"
                value={formData.ogTitle}
                onChange={(e) => setFormData({ ...formData, ogTitle: e.target.value })}
                placeholder="Title for social media sharing (optional - uses Page Title if empty)"
              />
              <small>
                Shown when your page is shared on Facebook, LinkedIn, WhatsApp, etc. 
                {!formData.ogTitle && <span className="hint"> Leave empty to use Page Title</span>}
              </small>
            </div>

            <div className="form-group">
              <label>OG Description</label>
              <textarea
                value={formData.ogDescription}
                onChange={(e) => setFormData({ ...formData, ogDescription: e.target.value })}
                rows="2"
                placeholder="Description for social media sharing (optional - uses Meta Description if empty)"
              />
              <small>
                Shown below the title when shared. 
                {!formData.ogDescription && <span className="hint"> Leave empty to use Meta Description</span>}
              </small>
            </div>

            <div className="form-group">
              <label>OG Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'og')}
                disabled={uploading}
              />
              {uploading && <p className="upload-status">Uploading...</p>}
              {formData.ogImage && (
                <div className="image-preview">
                  <img src={formData.ogImage} alt="OG Preview" />
                  <input
                    type="text"
                    value={formData.ogImage}
                    onChange={(e) => setFormData({ ...formData, ogImage: e.target.value })}
                    placeholder="Or paste image URL"
                    className="image-url-input"
                  />
                  <small>Current OG Image URL (editable)</small>
                </div>
              )}
              {!formData.ogImage && (
                <small className="hint">
                  Upload an image or paste a URL. Recommended: 1200x630px. 
                  <strong> This image appears when your page is shared on social media!</strong>
                </small>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>
              Twitter Cards
              <button
                type="button"
                className="help-icon"
                onClick={() => showHelp(
                  'What are Twitter Cards?',
                  `Twitter Cards control how your page appears when shared on Twitter/X.

Twitter Title: The title shown in Twitter cards. If left empty, OG Title or Page Title will be used.

Twitter Description: The description shown in Twitter cards. If left empty, OG Description or Meta Description will be used.

Twitter Image: The image displayed in Twitter cards. Recommended size: 1200x675 pixels. If left empty, OG Image will be used.

Why it matters: Twitter uses its own card format. While it often falls back to OG tags, having Twitter-specific tags ensures optimal display on Twitter/X.`
                )}
                title="What are Twitter Cards?"
              >
                ❓
              </button>
            </h3>
            
            <div className="form-group">
              <label>Twitter Title</label>
              <input
                type="text"
                value={formData.twitterTitle}
                onChange={(e) => setFormData({ ...formData, twitterTitle: e.target.value })}
                placeholder="Title for Twitter sharing (optional - uses OG Title if empty)"
              />
              <small>
                Shown when your page is shared on Twitter/X. 
                {!formData.twitterTitle && <span className="hint"> Leave empty to use OG Title or Page Title</span>}
              </small>
            </div>

            <div className="form-group">
              <label>Twitter Description</label>
              <textarea
                value={formData.twitterDescription}
                onChange={(e) => setFormData({ ...formData, twitterDescription: e.target.value })}
                rows="2"
                placeholder="Description for Twitter sharing (optional - uses OG Description if empty)"
              />
              <small>
                Shown in Twitter cards. 
                {!formData.twitterDescription && <span className="hint"> Leave empty to use OG Description or Meta Description</span>}
              </small>
            </div>

            <div className="form-group">
              <label>Twitter Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'twitter')}
                disabled={uploading}
              />
              {uploading && <p className="upload-status">Uploading...</p>}
              {formData.twitterImage && (
                <div className="image-preview">
                  <img src={formData.twitterImage} alt="Twitter Preview" />
                  <input
                    type="text"
                    value={formData.twitterImage}
                    onChange={(e) => setFormData({ ...formData, twitterImage: e.target.value })}
                    placeholder="Or paste image URL"
                    className="image-url-input"
                  />
                  <small>Current Twitter Image URL (editable)</small>
                </div>
              )}
              {!formData.twitterImage && (
                <small className="hint">
                  Upload an image or paste a URL. Recommended: 1200x675px. 
                  {!formData.ogImage && <span> If empty, OG Image will be used.</span>}
                </small>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>
              Structured Data (JSON-LD)
              <button
                type="button"
                className="help-icon"
                onClick={() => showHelp(
                  'What is Structured Data (JSON-LD)?',
                  `Structured Data (also called Schema.org or JSON-LD) helps search engines understand your content better.

It's code that describes your page in a format search engines can easily read. For example:
- Organization schema: Tells Google about your business
- Product schema: Helps Google show rich product information
- Article schema: Helps Google understand blog posts

Why it matters: Can enable rich snippets in search results (like star ratings, prices, etc.), which can increase click-through rates. This is advanced - you can leave it empty if you're not sure.`
                )}
                title="What is Structured Data?"
              >
                ❓
              </button>
            </h3>
            <button
              type="button"
              onClick={() => setShowStructuredData(!showStructuredData)}
              className="toggle-btn"
            >
              {showStructuredData ? 'Hide' : 'Show'} Structured Data Editor
            </button>
            
            {showStructuredData && (
              <div className="form-group">
                <label>JSON-LD Schema</label>
                <textarea
                  value={formData.structuredData ? JSON.stringify(formData.structuredData, null, 2) : ''}
                  onChange={handleStructuredDataChange}
                  rows="10"
                  placeholder='{"@context": "https://schema.org", "@type": "Organization", ...}'
                />
                <small>
                  Enter valid JSON-LD structured data. This is advanced - leave empty if unsure. 
                  Helps search engines understand your content and can enable rich snippets.
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive === true}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="checkbox-text">
                <strong>Active</strong> - Use these SEO settings (uncheck to disable)
              </span>
            </label>
            <small>
              {formData.isActive 
                ? '✓ These SEO settings will be used on the frontend' 
                : '✗ These SEO settings will NOT be used (inactive)'}
            </small>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Save SEO Settings
            </button>
            <button type="button" onClick={resetForm} className="btn-secondary">
              Reset
            </button>
          </div>
        </form>

        {/* SEO Settings List */}
        <div className="seo-list">
          <h3>All SEO Settings</h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="seo-grid">
              {pages.map((page) => {
                const setting = seoSettings.find((s) => s.page === page.value);
                const isActive = setting?.isActive !== undefined ? setting.isActive : false;
                return (
                  <div key={page.value} className="seo-card">
                    <div className="seo-card-header">
                      <h4>{page.label}</h4>
                      <span className={`status ${isActive ? 'active' : 'inactive'}`}>
                        {isActive ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </div>
                    {setting ? (
                      <>
                        <p className="seo-title">{setting.title || 'No title set'}</p>
                        <p className="seo-desc">{setting.description || 'No description set'}</p>
                        <div className="seo-meta">
                          <small>Keywords: {setting.keywords || 'None'}</small>
                          <small>Updated: {setting.updatedAt ? new Date(setting.updatedAt).toLocaleDateString() : 'Never'}</small>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedPage(page.value);
                            // loadPageSettings will be called by useEffect, no need to call it here
                          }}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="no-settings">No SEO settings configured</p>
                        <button
                          onClick={() => {
                            setSelectedPage(page.value);
                            // loadPageSettings will be called by useEffect, no need to call it here
                          }}
                          className="btn-create"
                        >
                          Create Settings
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Help Modal */}
      {helpModal.show && (
        <div className="help-modal-overlay" onClick={closeHelp}>
          <div className="help-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h3>{helpModal.title}</h3>
              <button className="help-modal-close" onClick={closeHelp}>×</button>
            </div>
            <div className="help-modal-body">
              <p style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>{helpModal.content}</p>
            </div>
            <div className="help-modal-footer">
              <button className="btn-primary" onClick={closeHelp}>Got it!</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOManager;

